import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { generateTypes } from "./generateTypes";
import "./JsonToTypeScript.css";
const sample = JSON.stringify(
  {
    id: 42,
    name: "Ada Lovelace",
    active: true,
    team: { name: "Platform", location: null },
    projects: [
      { id: 1, name: "Launchpad", language: "TypeScript" },
      { id: 2, name: "Toolbox" },
    ],
    skills: ["TypeScript", "React"],
  },
  null,
  2,
);
export default function JsonToTypeScript() {
  const [input, setInput] = useState(sample);
  const [rootName, setRootName] = useState("ApiResponse");
  const [readonly, setReadonly] = useState(false);
  const [notice, setNotice] = useState("");
  const result = useMemo(() => {
    try {
      return { code: generateTypes(input, { rootName, readonly }), error: "" };
    } catch (error) {
      return {
        code: "",
        error:
          error instanceof Error
            ? error.message
            : "Unable to convert this JSON.",
      };
    }
  }, [input, rootName, readonly]);
  function changeInput(value: string) {
    setInput(value);
    setNotice("");
  }
  async function copy() {
    try {
      await navigator.clipboard.writeText(result.code);
      setNotice("TypeScript copied to clipboard.");
    } catch {
      setNotice(
        "Clipboard access is unavailable. Select the output and copy it manually.",
      );
    }
  }
  function download() {
    const url = URL.createObjectURL(
      new Blob([result.code], { type: "text/plain;charset=utf-8" }),
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `${rootName.trim()}.ts`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setNotice("Your TypeScript file is ready to download.");
  }
  function format() {
    try {
      changeInput(JSON.stringify(JSON.parse(input), null, 2));
    } catch {
      setNotice("Fix the JSON error before formatting.");
    }
  }
  return (
    <div className="ed-wrap ed-page converter-page">
      <Helmet>
        <title>
          JSON to TypeScript Converter — Free Developer Tool | Nuhman
        </title>
        <meta
          name="description"
          content="Convert JSON into TypeScript interfaces for nested objects and arrays. Copy or download typed code. Free, with no signup; conversion stays in your browser."
        />
      </Helmet>
      <nav className="converter-breadcrumb" aria-label="Breadcrumb">
        <Link to="/smart-tools">Tools & utilities</Link>
        <span>/</span>
        <span>JSON → TypeScript</span>
      </nav>
      <header className="converter-heading">
        <p className="ed-eyebrow">
          <span className="ed-dot" />
          Developer toolkit · Code generation
        </p>
        <h1>
          From JSON.
          <br />
          <em>To your next build.</em>
        </h1>
        <p>
          Turn an API response into TypeScript interfaces.
          <br />
          Nested objects, mixed arrays, and optional fields — handled.
        </p>
        <span className="converter-private">
          ↳ Runs in your browser. Your JSON is never uploaded.
        </span>
      </header>
      <section
        className="converter-workbench"
        aria-label="JSON to TypeScript converter"
      >
        <div className="converter-options">
          <label>
            Root type name
            <input
              value={rootName}
              onChange={(e) => {
                setRootName(e.target.value);
                setNotice("");
              }}
              spellCheck={false}
              maxLength={80}
            />
          </label>
          <label className="converter-checkbox">
            <input
              type="checkbox"
              checked={readonly}
              onChange={(e) => {
                setReadonly(e.target.checked);
                setNotice("");
              }}
            />
            Readonly properties
          </label>
          <span>Free · No account needed</span>
        </div>
        <div className="converter-editors">
          <section className="converter-pane">
            <div className="converter-pane-heading">
              <h2>
                <span>01</span> Your JSON
              </h2>
              <div>
                <button onClick={() => changeInput(sample)}>
                  Load example
                </button>
                <button onClick={() => changeInput("")}>Clear</button>
              </div>
            </div>
            <label className="converter-sr" htmlFor="json-source">
              JSON input
            </label>
            <textarea
              id="json-source"
              value={input}
              onChange={(e) => changeInput(e.target.value)}
              spellCheck={false}
              maxLength={250001}
              aria-describedby={
                result.error && input.trim() ? "converter-error" : undefined
              }
              aria-invalid={!!result.error && !!input.trim()}
              placeholder={"Paste your JSON here…"}
            />
            <div className="converter-pane-footer">
              <span>{input.length.toLocaleString()} characters</span>
              <button
                onClick={format}
                disabled={!input.trim() || !!result.error}
              >
                Format JSON
              </button>
            </div>
          </section>
          <section className="converter-pane converter-output">
            <div className="converter-pane-heading">
              <h2>
                <span>02</span> Your TypeScript
              </h2>
              <span className="converter-file">.ts</span>
            </div>
            <label className="converter-sr" htmlFor="typescript-output">
              TypeScript output
            </label>
            <textarea
              id="typescript-output"
              readOnly
              value={result.code}
              spellCheck={false}
              placeholder="Your generated types will appear here."
            />
            <div className="converter-pane-footer">
              <span>
                {result.code ? "Ready to use" : "Waiting for valid JSON"}
              </span>
              <span>TypeScript</span>
            </div>
          </section>
        </div>
        {result.error && input.trim() && (
          <p className="converter-error" id="converter-error" role="alert">
            {result.error}
          </p>
        )}
        <div className="converter-actions">
          <p role="status">
            {notice || "Nothing is saved when you leave this page."}
          </p>
          <div>
            <button
              className="converter-secondary"
              onClick={download}
              disabled={!result.code}
            >
              Download .ts ↓
            </button>
            <button
              className="ed-button"
              onClick={copy}
              disabled={!result.code}
            >
              Copy TypeScript ↗
            </button>
          </div>
        </div>
      </section>
      <section className="converter-notes">
        <article>
          <p className="ed-eyebrow">Built for real responses</p>
          <h2>
            Less typing.
            <br />
            More building.
          </h2>
          <p>
            Paste JSON from your API, name the root type, and take the
            interfaces straight into your project.
          </p>
        </article>
        <article>
          <h3>Nested objects, neatly typed</h3>
          <p>
            Objects become named interfaces. Arrays are checked across all
            supplied records; missing fields become optional.
          </p>
          <h3>Honest about the unknowns</h3>
          <p>
            Empty arrays use <code>unknown</code>. Null stays null. Mixed values
            produce union types.
          </p>
        </article>
        <article>
          <h3>A starting point, not a schema</h3>
          <p>
            Types reflect this sample only. Review them against your API
            contract. Generated types do not validate data at runtime.
          </p>
          <h3>Your data stays with you</h3>
          <p>
            No AI requests, uploads, account, or saved history. Everything runs
            on this device.
          </p>
        </article>
      </section>
      <Link className="converter-back" to="/smart-tools">
        ← Explore all developer tools
      </Link>
    </div>
  );
}
